import constate from 'constate';
import { useState } from 'react';

import { IStep, IStepButtonOptions } from 'core/types';
import { clearSubmitIntent } from 'core/utils';

import { useNotificationContext } from './notification';

interface IProps {
  steps: IStep[];
  onClose?: () => void;
}

const useSteps = ({ steps, onClose }: IProps) => {
  const [activeStep, setActiveStep] = useState<IStep>(steps[0]);
  const [isLoadingNextStep, setIsLoadingNextStep] = useState(false);

  const { clearDrawerNotification, clearFormNotification } = useNotificationContext();

  const totalSteps = steps.length;
  const activeStepIndex = steps.findIndex((step) => activeStep?.type === step?.type);

  const goToPreviousStep = ({ onClick = null, stepType = null }: IStepButtonOptions = {}) => {
    setIsLoadingNextStep(true);

    if (onClick) {
      onClick();
    }

    if (stepType) {
      goToStep(stepType);

      return;
    }

    goToActiveStep(steps[activeStepIndex - 1]);
  };

  const goToNextStep = async ({ onClick = null, stepType = null }: IStepButtonOptions = {}) => {
    setIsLoadingNextStep(true);

    const isFinalStep = activeStepIndex === totalSteps - 1;

    if (onClick) {
      let enableNextStep = undefined;

      try {
        enableNextStep = await onClick(handleOnClose);
      } finally {
        setIsLoadingNextStep(false);
      }

      if (typeof enableNextStep === 'string') {
        goToStep(enableNextStep);

        return;
      }

      if (false === enableNextStep) {
        // The submit was a no-op, so no teardown blur is coming to consume the latch marked on
        // mousedown/Enter. Disarm it here or it would suppress the next unrelated blur's preview.
        clearSubmitIntent();

        return;
      }
    }

    if (stepType) {
      goToStep(stepType);

      return;
    }

    if (!isFinalStep) {
      goToActiveStep(steps[activeStepIndex + 1]);

      return;
    }

    setTimeout(() => {
      setIsLoadingNextStep(false);

      handleOnClose(activeStep?.cancelButton?.options);
    }, 1000);
  };

  const goToStep = (stepType: string) => {
    const stepToGoTo = steps.filter((step) => stepType === step.type);

    goToActiveStep(stepToGoTo[0]);
    setIsLoadingNextStep(false);
  };

  const goToActiveStep = (step: IStep) => {
    setActiveStep(step);
    setIsLoadingNextStep(false);

    clearDrawerNotification();
    clearFormNotification();
  };

  const handleOnClose = async ({ onClick = null }: IStepButtonOptions = {}) => {
    if (onClick) {
      await onClick();
    }

    goToActiveStep(steps[0]);
    setIsLoadingNextStep(false);

    onClose();
  };

  return {
    steps,
    activeStep,
    activeStepIndex,
    totalSteps,
    goToStep,
    goToPreviousStep,
    goToNextStep,
    handleOnClose,
    isFinalStep: activeStepIndex === totalSteps - 1,
    isLoadingNextStep,
  };
};

export const [StepsProvider, useStepsContext] = constate(useSteps);
